// src/auth/authService.js
//
// Thin wrapper around MSAL. Everything that touches the Microsoft identity
// platform goes through here so components never import MSAL directly.

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import { reactive, readonly } from 'vue'
import { hasDisplayKey } from './displayKey'
import { msalConfig, loginRequest, ADMIN_GROUP_ID, DISPLAY_GROUP_ID } from './msalConfig'

const msalInstance = new PublicClientApplication(msalConfig)

// Reactive state components can bind to.
const state = reactive({
  initialized: false,
  account: null,      // the signed-in Entra account, or null
  error: null
})

export const authState = readonly(state)

/**
 * Must be awaited once before any other MSAL call. MSAL v3+ requires an
 * explicit initialize(); calling acquireToken or login before it throws.
 *
 * Also processes the redirect response when the browser comes back from the
 * Microsoft login page.
 */
export async function initialize() {
  if (state.initialized) return

  await msalInstance.initialize()

  // If we have just returned from a login redirect, this resolves with the
  // result. Returns null on a normal page load.
  const response = await msalInstance.handleRedirectPromise()

  if (response?.account) {
    msalInstance.setActiveAccount(response.account)
    state.account = response.account
  } else {
    // Restore an existing session if one is cached.
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0])
      state.account = accounts[0]
    }
  }

  state.initialized = true

  // One line, once, saying how the role was decided. Cheap, and it turns three
  // indistinguishable failures into an obvious one.
  if (state.account) {
    const why = explainRole()
    console.info('[Vero] role:', why.resolved, why)

    if (!why.adminGroupConfigured) {
      console.warn(
        '[Vero] VUE_APP_ADMIN_GROUP_ID is not set, so nobody can be an admin. ' +
        'Add it to .env.local and restart the dev server -- these are baked in ' +
        'at build time, so a reload alone will not pick it up.'
      )
    } else if (why.groupsInToken === undefined) {
      console.warn(
        '[Vero] The token has no groups claim. Check Token configuration in the ' +
        'app registration, then sign out and back in.'
      )
    }
  }
}

/**
 * Start the sign-in flow. Uses redirect rather than popup: popups are blocked
 * by default in several browsers and behave badly on mobile.
 */
export async function login() {
  state.error = null
  await msalInstance.loginRedirect(loginRequest)
  // Execution does not continue past here; the browser navigates away.
}

export async function logout() {
  state.account = null
  await msalInstance.logoutRedirect()
}

/**
 * Acquire an access token for the given scopes.
 *
 * Tries silently first (from cache or via a hidden iframe refresh). Falls back
 * to a redirect only when Entra says interaction is genuinely required, e.g.
 * consent needed or MFA challenge.
 */
export async function getToken(scopes) {
  const account = msalInstance.getActiveAccount()
  if (!account) throw new Error('Not signed in')

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account })
    return result
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes, account })
      return null
    }
    throw err
  }
}

/**
 * The raw ID token. This is what gets sent to the Vero backend, which validates
 * its signature against Microsoft's public keys and then issues its own JWT.
 *
 * Note: the ID token identifies the user. It is deliberately NOT the access
 * token, which is scoped to Graph and is not ours to validate.
 */
export async function getIdToken() {
  const result = await getToken(loginRequest.scopes)
  return result?.idToken ?? null
}

export function getAccount() {
  return msalInstance.getActiveAccount()
}

export function isSignedIn() {
  // A paired screen has no Microsoft account and never will. It is signed in
  // by virtue of holding a key, which the backend checks on every request.
  if (hasDisplayKey()) return true
  return msalInstance.getActiveAccount() !== null
}

/**
 * The caller's role, from their Entra security group membership.
 *
 * This is for hiding menu items and pages the person cannot use. It is NOT the
 * security boundary: the backend derives the same role from the same token and
 * refuses anything out of scope, so editing this in the browser gains nothing.
 *
 * Admin wins where somebody is in both groups, matching the backend.
 *
 * Reads state.account rather than msalInstance directly. That matters: the
 * account object from MSAL is not reactive, so a computed() calling this had no
 * dependency to invalidate on and Vue cached the first answer forever. A menu
 * built that way never notices the account arriving.
 */
export function role() {
  // A paired screen is the display, whatever else might be in storage. The
  // backend enforces the same: a key maps to the display role and nothing
  // more, so this only decides what the shell draws.
  if (hasDisplayKey()) return 'display'

  const groups = state.account?.idTokenClaims?.groups ?? []

  if (ADMIN_GROUP_ID && groups.includes(ADMIN_GROUP_ID)) return 'admin'
  if (DISPLAY_GROUP_ID && groups.includes(DISPLAY_GROUP_ID)) return 'display'
  return 'user'
}

/**
 * Why the role came out the way it did.
 *
 * Three things can make an admin look like an ordinary user, and they are hard
 * to tell apart from the outside: the group ids are missing from the build, the
 * token predates being added to the group, or the groups claim is not being
 * emitted at all. This says which.
 */
export function explainRole() {
  const claims = state.account?.idTokenClaims

  return {
    resolved: role(),
    signedIn: Boolean(state.account),
    // undefined means the claim was never emitted; an empty array means it was
    // emitted and the person is in no groups.
    groupsInToken: claims?.groups,
    adminGroupConfigured: Boolean(ADMIN_GROUP_ID),
    displayGroupConfigured: Boolean(DISPLAY_GROUP_ID),
    // A token issued before the group was added will not contain it. Signing
    // out and back in is the fix.
    tokenIssuedAt: claims?.iat ? new Date(claims.iat * 1000).toISOString() : null
  }
}

export function isAdmin() {
  return role() === 'admin'
}

export { msalInstance }
